import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle } from 'lucide-react';
import SEO from '../../components/SEO';
import SeoNav from '../../components/SeoNav';
import BlogCTA from '../../components/BlogCTA';
import RelatedArticles from '../../components/RelatedArticles';

const schema = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Article',
      'headline': 'UK VAT Invoice Requirements 2025 — Full, Simplified & Modified',
      'description': 'Complete guide to HMRC VAT invoice requirements: all 13 mandatory fields for a full VAT invoice, simplified invoice rules under £250, modified invoices, time limits, and record-keeping.',
      'url': 'https://www.invilic.com/blog/vat-invoice-requirements-uk',
      'datePublished': '2025-05-20',
      'dateModified': '2026-06-01',
      'author': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
      'publisher': { '@type': 'Organization', 'name': 'Invilic', 'url': 'https://www.invilic.com' },
    },
    {
      '@type': 'FAQPage',
      'mainEntity': [
        {
          '@type': 'Question',
          'name': 'What are the mandatory fields on a UK VAT invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A full UK VAT invoice must include: (1) the words "VAT Invoice," (2) a unique sequential invoice number, (3) the invoice date, (4) the tax point if different from the invoice date, (5) your full name and address, (6) your VAT registration number, (7) your customer\'s name and address, (8) a description of the goods or services, (9) quantity and unit price per item, (10) the VAT rate applied, (11) the total excluding VAT, (12) the total VAT charged, and (13) the total amount payable including VAT.' },
        },
        {
          '@type': 'Question',
          'name': 'When must you issue a VAT invoice in the UK?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'You must issue a VAT invoice within 30 days of the date of supply (when goods were delivered or services performed). For continuous supplies, the tax point is determined by the invoice or payment date, whichever comes first. Failing to issue a VAT invoice on time is an offence under UK VAT law.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the VAT registration threshold in the UK in 2025?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'The UK VAT registration threshold is £90,000 as of April 2024. If your taxable turnover in any 12-month rolling period exceeds £90,000, you must register for VAT within 30 days. Voluntary registration is available below this threshold.' },
        },
        {
          '@type': 'Question',
          'name': 'Do I need to issue a VAT invoice if I am not VAT-registered?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'No. If you are not VAT-registered, you cannot issue a VAT invoice or charge VAT. You issue a standard invoice without any VAT charge. Charging VAT when not registered is fraudulent under UK law. Simply issue a regular invoice with your business name, address, description of services, and the amount due — no VAT number or VAT amount.' },
        },
        {
          '@type': 'Question',
          'name': 'What is the difference between a full and simplified VAT invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A full VAT invoice is required for most B2B transactions and must contain 13 mandatory fields including the customer\'s name and address, the net amount, and the VAT amount as a separate line. A simplified VAT invoice can only be used for retail supplies where the VAT-inclusive total does not exceed £250. It requires fewer fields and does not need the customer\'s name and address or a separate VAT amount — just the total including VAT and the VAT rate.' },
        },
        {
          '@type': 'Question',
          'name': 'What happens if my VAT invoice is incorrect or incomplete?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'An incorrect or incomplete VAT invoice means your customer cannot reclaim the input VAT shown. HMRC can deny input tax credit claims based on invalid invoices. If you discover an error, issue a credit note to cancel the original and reissue a corrected invoice. Keep records of all invoices, including corrections, for at least 6 years.' },
        },
        {
          '@type': 'Question',
          'name': 'What is a modified VAT invoice?',
          'acceptedAnswer': { '@type': 'Answer', 'text': 'A modified VAT invoice is used by retailers for supplies over £250. It includes all the fields of a full VAT invoice except that it shows VAT-inclusive prices rather than net prices, provided the VAT rate and VAT amount are shown separately. Most businesses do not use modified invoices — they are primarily for retail environments where showing VAT-inclusive shelf prices is standard practice.' },
        },
      ],
    },
  ],
};

const faqs = schema['@graph'][1].mainEntity;

const fullVatFields = [
  { field: 'The words "VAT Invoice" prominently displayed', note: null },
  { field: 'A unique sequential invoice number', note: null },
  { field: 'The invoice date', note: null },
  { field: 'The tax point (date of supply) — if different from the invoice date', note: 'Required when goods/services were supplied on a different date' },
  { field: 'Your full name (or trading name) and address', note: null },
  { field: 'Your VAT registration number', note: 'Format: GB followed by 9 digits, e.g. GB 123 4567 89' },
  { field: "Your customer's full name and address", note: 'Required for full invoices — not needed on simplified invoices' },
  { field: 'A description sufficient to identify the goods or services supplied', note: null },
  { field: 'For each line: quantity, unit price, and applicable VAT rate', note: null },
  { field: 'The total amount excluding VAT (net)', note: null },
  { field: 'The rate(s) of VAT and the total VAT amount charged', note: null },
  { field: 'The total amount payable including VAT (gross)', note: null },
  { field: 'For mixed VAT rates: a subtotal for each rate showing net, VAT rate, and VAT amount', note: 'Only required when multiple VAT rates apply on the same invoice' },
];

const simplifiedFields = [
  'The words "VAT Invoice"',
  'Your name, address, and VAT registration number',
  'The time of supply (tax point)',
  'A description of the goods or services',
  'The VAT rate applicable to each item',
  'The total amount payable including VAT',
];

const invoiceTypeComparison = [
  {
    type: 'Full VAT Invoice',
    threshold: 'All B2B transactions',
    customerName: 'Required',
    netAmount: 'Required',
    vatAmount: 'Required (separate line)',
    useCase: 'Standard for all VAT-registered B2B suppliers',
  },
  {
    type: 'Simplified VAT Invoice',
    threshold: 'Retail supplies up to £250',
    customerName: 'Not required',
    netAmount: 'Not required',
    vatAmount: 'Rate shown, amount not separated',
    useCase: 'Retailers, taxis, parking, fast food',
  },
  {
    type: 'Modified VAT Invoice',
    threshold: 'Retail supplies over £250',
    customerName: 'Required',
    netAmount: 'VAT-inclusive prices permitted',
    vatAmount: 'Required (separate line)',
    useCase: 'Retailers who display VAT-inclusive prices',
  },
];

export default function UkVatInvoiceRequirementsPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-gray-950">
      <SEO
        title="UK VAT Invoice Requirements 2025 — Full, Simplified & Modified | Invilic Blog"
        description="Complete HMRC VAT invoice guide: all 13 mandatory fields, simplified invoice rules under £250, modified invoices, the 30-day time limit, and 6-year record-keeping rules."
        canonical="https://www.invilic.com/blog/vat-invoice-requirements-uk"
        ogType="article"
        schema={schema}
      />
      <SeoNav />

      <main className="max-w-3xl mx-auto px-6 py-16">
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-5 text-sm">
            <Link to="/blog" className="text-blue-600 dark:text-blue-400 hover:underline">Blog</Link>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="text-gray-500 dark:text-gray-400">UK VAT</span>
          </div>
          <span className="inline-block bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold px-2.5 py-1 rounded-full mb-4">UK VAT</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            UK VAT Invoice Requirements 2025 — Full, Simplified &amp; Modified
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm">May 20, 2025 · 8 min read</p>
        </header>

        <article className="text-gray-700 dark:text-gray-300 leading-relaxed">

          <p className="text-lg mb-6">
            A UK VAT invoice must contain 13 mandatory fields set out by HMRC — including your VAT registration number, the tax point, a line-by-line breakdown of VAT, and the gross total. If any field is missing, your customer cannot legally reclaim the input VAT, and HMRC can reject their claim during an audit.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Who Must Issue VAT Invoices in the UK?</h2>
          <p className="mb-4">
            Only VAT-registered businesses can issue VAT invoices. The UK VAT registration threshold is <strong className="font-semibold text-gray-900 dark:text-white">£90,000</strong> of taxable turnover in any rolling 12-month period (as of April 2024). Once you exceed this, you must register within 30 days.
          </p>
          <p className="mb-4">
            Below the threshold, you do not charge VAT and do not issue VAT invoices. You issue standard invoices — no VAT number, no VAT line. Many small businesses voluntarily register for VAT below the threshold to reclaim input VAT on purchases. Once registered, you must issue VAT invoices for all taxable supplies.
          </p>
          <p className="mb-6">
            If a customer asks you for a VAT invoice and you are not VAT-registered, you cannot issue one. Doing so — and charging VAT you then keep — is a criminal offence under the Value Added Tax Act 1994.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">The Three Types of UK VAT Invoice</h2>
          <p className="mb-4">HMRC recognises three types of VAT invoice. Most businesses only ever use the full VAT invoice.</p>
          <div className="overflow-x-auto mb-8">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Type</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">When Allowed</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Customer Name?</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Separate VAT?</th>
                </tr>
              </thead>
              <tbody>
                {invoiceTypeComparison.map(({ type, threshold, customerName, vatAmount }) => (
                  <tr key={type} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{type}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{threshold}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{customerName}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{vatAmount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Full VAT Invoice — All 13 Mandatory Fields</h2>
          <p className="mb-4">
            A full VAT invoice is required for all B2B transactions where the customer wants to reclaim input VAT. It must contain every one of the following:
          </p>
          <div className="space-y-2 mb-8">
            {fullVatFields.map(({ field, note }, i) => (
              <div key={field} className="flex items-start gap-2 text-sm">
                <div className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 flex items-center justify-center text-xs font-bold mt-0.5">{i + 1}</div>
                <div>
                  <span>{field}</span>
                  {note && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{note}</p>}
                </div>
              </div>
            ))}
          </div>

          <BlogCTA
            text="Create a HMRC-compliant UK VAT invoice with all mandatory fields — 20% VAT auto-calculated, PDF download free."
            to="/invoice-generator-uk"
            btnText="Create UK VAT Invoice Free"
          />

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Simplified VAT Invoice — for Retail Supplies Under £250</h2>
          <p className="mb-4">
            For retail supplies where the VAT-inclusive total does not exceed <strong className="font-semibold text-gray-900 dark:text-white">£250</strong>, you may issue a simplified VAT invoice. This is common for shops, taxis, parking, and fast-food businesses where issuing a full invoice for every transaction is impractical.
          </p>
          <p className="mb-3">A simplified VAT invoice only needs:</p>
          <div className="space-y-2 mb-4">
            {simplifiedFields.map(field => (
              <div key={field} className="flex items-start gap-2 text-sm">
                <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
                <span>{field}</span>
              </div>
            ))}
          </div>
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800/30 rounded-xl mb-8 text-sm">
            <strong className="font-semibold text-gray-900 dark:text-white">Important:</strong> A simplified invoice does not show the customer's name and address or the net/VAT amounts separately. If your B2B customer needs to reclaim the full input VAT and requires a document showing the net and VAT split, they may request a full invoice instead — even for amounts under £250. Always check with your client.
          </div>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Modified VAT Invoice — for Retailers Over £250</h2>
          <p className="mb-4">
            A modified VAT invoice is used by retailers for supplies over £250. It includes all the fields of a full VAT invoice but allows VAT-inclusive prices to be shown (rather than net prices), as long as the VAT rate and VAT amount are still shown separately.
          </p>
          <p className="mb-6">
            Most professional service businesses — freelancers, consultants, agencies — never need modified invoices. They are mainly used in retail environments where shelf prices already include VAT. Unless you are running a retail shop, use a full VAT invoice for all transactions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">UK VAT Rates in 2025</h2>
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-900/50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Rate</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">%</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">Applies to</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { rate: 'Standard rate', pct: '20%', applies: 'Most goods and services' },
                  { rate: 'Reduced rate', pct: '5%', applies: "Home energy, children's car seats, some residential renovations" },
                  { rate: 'Zero rate', pct: '0%', applies: "Most food, children's clothing, books, newspapers, exports" },
                  { rate: 'Exempt', pct: '—', applies: 'Financial services, insurance, postage, health and education services' },
                ].map(({ rate, pct, applies }) => (
                  <tr key={rate} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white border border-gray-100 dark:border-gray-800">{rate}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{pct}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 border border-gray-100 dark:border-gray-800">{applies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mb-6 text-sm">
            Note: Zero-rated and exempt supplies are different. Zero-rated supplies must appear on your VAT return (they count towards your taxable turnover). Exempt supplies do not. If you only make exempt supplies, you cannot register for VAT. If you make a mix, the rules become more complex — seek advice from an accountant.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">Time Limit for Issuing a VAT Invoice</h2>
          <p className="mb-4">
            You must issue a VAT invoice within <strong className="font-semibold text-gray-900 dark:text-white">30 days of the tax point</strong> (the date goods were delivered or services were completed). For continuous supplies of services, the tax point is usually the earlier of the invoice date or the payment date.
          </p>
          <p className="mb-6">
            Missing the 30-day window is a VAT offence. HMRC can impose a penalty for persistent late invoicing. Get into the habit of invoicing on the same day work is completed or delivered — this also means you get paid faster.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">VAT Invoices for Exports and Zero-Rated Supplies</h2>
          <p className="mb-4">
            When you export goods or supply certain zero-rated services to overseas customers, you still issue a VAT invoice — but with 0% VAT applied. You must keep evidence that the goods were exported (such as shipping documents or customs declarations) to support the zero rating.
          </p>
          <p className="mb-6">
            For B2B services supplied to businesses in other countries, the reverse charge mechanism often applies — meaning your customer accounts for the VAT in their own country rather than you charging it. In these cases, your invoice should state "Reverse charge applies" and show 0% VAT.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">How Long to Keep UK VAT Records</h2>
          <p className="mb-6">
            HMRC requires you to keep all VAT records — including sales invoices, purchase invoices, and VAT account records — for at least <strong className="font-semibold text-gray-900 dark:text-white">6 years</strong>. For capital assets subject to the Capital Goods Scheme (e.g. commercial property or computers over £50,000), records may need to be kept for up to 10 years. Digital copies are acceptable under Making Tax Digital (MTD).
          </p>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-4">What If Your Invoice Is Wrong?</h2>
          <p className="mb-4">
            If you issue an incorrect or incomplete VAT invoice, do not simply delete it or issue a replacement that reuses the same invoice number. The correct process:
          </p>
          <ol className="space-y-2 mb-8 list-decimal list-inside text-sm">
            <li className="leading-relaxed">Issue a <strong className="font-semibold text-gray-900 dark:text-white">credit note</strong> that clearly references the original invoice number and cancels the full amount.</li>
            <li className="leading-relaxed">Issue a <strong className="font-semibold text-gray-900 dark:text-white">new invoice</strong> with a new invoice number containing the correct information.</li>
            <li className="leading-relaxed">Keep all three documents (original, credit note, corrected invoice) in your records for 6 years.</li>
          </ol>

          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-10 mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map(({ name, acceptedAnswer }) => (
              <details key={name} className="group border border-gray-100 dark:border-gray-800 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-medium text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-900/50 list-none text-sm">
                  {name}
                  <ArrowRight className="w-4 h-4 text-gray-400 transition-transform group-open:rotate-90 flex-shrink-0 ml-4" />
                </summary>
                <p className="px-5 pb-4 text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{acceptedAnswer.text}</p>
              </details>
            ))}
          </div>

          <BlogCTA
            text="Create a HMRC-compliant UK VAT invoice — all 13 mandatory fields, 20% VAT auto-calculated. Free, no sign-up required."
            to="/invoice-generator-uk"
            btnText="Create UK VAT Invoice"
          />

        </article>
        <RelatedArticles current="/blog/vat-invoice-requirements-uk" />
      </main>
    </div>
  );
}
